import { m } from '@sohanscript/i18n';
import React, { useMemo } from 'react';
import type { LinkProps } from '#/components/link.tsx';
import {
	getNavLabel,
	NAV_BRAND_ITEMS,
	NAV_FOOTER_ITEMS,
	NAV_ITEMS,
	type NavItemConfig,
} from '#/lib/config/nav';
import { getAppEnv } from '../utils/app-env';

export type Skill = { name: string; level: number };
export type Education = {
	degree: string;
	institution: string;
	period: string;
	description: string;
};

export type Deliverable = { id: string; text: string };

export type Experience = {
	id: string;
	role: string;
	company: string;
	location: string;
	period: string;
	summary: string;
	metrics: string;
	bullets: string[];
	stack: string[];
	deliverables: Deliverable[];
};

export type Recognition = {
	title: string;
	platform: string;
	year: string;
	note: string;
};

type LinkTypes = {
	labelKey: string;
	label: string;
	href: string;
	subhref: string;
	icon: string;
	opts?: LinkProps & { footerOnly?: boolean };
};
const DEFAULT_LINKS: NavItemConfig[] = NAV_ITEMS;

export const experiencesData: Experience[] = [
	{
		id: 'exp-keywords',
		role: 'Software Engineer',
		company: 'Keywords.am',
		location: 'Houston, Texas',
		period: 'OCT 2024 – CURRENT',
		summary:
			'Building a type-safe SaaS engine across Next.js, React, and tRPC with marketplace-aware AI workflows.',
		metrics: '3,000+ brands supported',
		bullets: [
			'Architected full-stack systems with Next.js 16, React 19, and tRPC v11',
			'Developed the AI-powered TFSD listing editor',
			'Shipped localized UI across 21 Amazon marketplaces',
			'Owned DX tooling with Biome, tsgo, Vitest, and Playwright',
		],
		stack: ['Next.js', 'React', 'tRPC', 'AI', 'OTel'],
		deliverables: [
			{
				id: 'd1',
				text: 'Built a type-safe full-stack architecture with Next.js 16, React 19, and tRPC v11',
			},
			{
				id: 'd2',
				text: 'Developed the AI-powered TFSD listing editor',
			},
			{
				id: 'd3',
				text: 'Shipped localization-aware UI across 21 Amazon marketplaces',
			},
			{
				id: 'd4',
				text: 'Owned DX tooling with Biome, tsgo, Vitest, and Playwright for code quality and testing',
			},
			{
				id: 'd5',
				text: 'Integrated billing, analytics, and OTel across a SaaS used by 3,000+ brands',
			},
		],
	},
	{
		id: 'exp-1',
		role: 'Front-end Lead',
		company: 'Findus Proptech',
		location: 'Monsheim, Germany',
		period: 'FEB 2024 – OCT 2024',
		summary:
			'Led the front-end architecture for property technology products with disciplined systems and polished UI delivery.',
		metrics: 'Design system leadership',
		bullets: [
			'Led front-end architecture with TypeScript and React',
			'Mentored junior developers and enforced code quality standards',
			'Delivered responsive, pixel-perfect UI using Tailwind CSS',
			'Drove design system consistency across the team',
		],
		stack: ['TypeScript', 'React', 'Tailwind', 'Architecture'],
		deliverables: [
			{
				id: 'd1',
				text: 'Led front-end architecture using TypeScript and React',
			},
			{
				id: 'd2',
				text: 'Enforced code quality standards and mentored junior developers',
			},
			{
				id: 'd3',
				text: 'Delivered responsive, pixel-perfect UI using Tailwind CSS',
			},
			{
				id: 'd4',
				text: 'Drove team-wide adoption of best practices and design system consistency',
			},
		],
	},
	{
		id: 'exp-2',
		role: 'Software Engineer',
		company: 'Level7 Interactive',
		location: 'Dhaka, Bangladesh',
		period: 'DEC 2023 – JAN 2024',
		summary:
			'Built production-ready interfaces with secure API integration, accessibility focus, and performance-minded execution.',
		metrics: 'Production-grade delivery',
		bullets: [
			'Built production-ready UI with React and Next.js',
			'Integrated REST and GraphQL APIs with secure JWT authentication',
			'Improved performance and accessibility compliance',
			'Collaborated across design and engineering handoff cycles',
		],
		stack: ['React', 'Next.js', 'GraphQL', 'REST', 'JWT'],
		deliverables: [
			{ id: 'd5', text: 'Built production-ready UI with React / Next.js' },
			{
				id: 'd6',
				text: 'Integrated REST and GraphQL APIs with secure JWT authentication',
			},
			{
				id: 'd7',
				text: 'Focused on performance optimization and accessibility compliance',
			},
		],
	},
	{
		id: 'exp-3',
		role: 'App Developer',
		company: 'Trendmover',
		location: 'Munich, Germany',
		period: 'DEC 2022 – MAR 2023',
		summary:
			'Shipped React Native product features end-to-end with scalable state management and tighter mobile UX.',
		metrics: 'Mobile app delivery',
		bullets: [
			'Developed and shipped React Native features end-to-end',
			'Implemented Redux Toolkit for scalable state management',
			'Improved app stability through refactoring',
			'Worked closely with design to refine mobile interactions',
		],
		stack: ['React Native', 'Redux', 'Mobile', 'UX'],
		deliverables: [
			{
				id: 'd8',
				text: 'Developed and shipped React Native features end-to-end',
			},
			{
				id: 'd9',
				text: 'Implemented Redux Toolkit for scalable state management',
			},
			{
				id: 'd10',
				text: 'Improved app stability and UX through refactoring and close designer collaboration',
			},
		],
	},
	// {
	// 	id: "exp-4",
	// 	role: "Frontend Web Developer",
	// 	company: "Adapted Mind",
	// 	location: "Nagpur, India",
	// 	period: "October 2022 – December 2022",
	// 	deliverables: [
	// 		{
	// 			id: "d11",
	// 			text: "Built SEO-friendly Next.js pages with optimized Core Web Vitals",
	// 		},
	// 		{
	// 			id: "d12",
	// 			text: "Created reusable component library with Tailwind CSS",
	// 		},
	// 		{
	// 			id: "d13",
	// 			text: "Implemented Redux-based user flows with responsive layouts",
	// 		},
	// 	],
	// },
];

const EXPERIENCE_MESSAGE_IDS = ['keywords', '1', '2', '3'] as const;

function experienceMessage(key: string) {
	return (m as unknown as Record<string, () => string>)[key]();
}

export function getLocalizedExperiences() {
	return experiencesData.map((experience, index) => {
		const id = EXPERIENCE_MESSAGE_IDS[index];
		const messagePrefix =
			id === 'keywords'
				? 'experience_keywords'
				: id === '1'
					? 'experience_findus_proptech'
					: id === '2'
						? 'experience_level7_interactive'
						: 'experience_trendmover';
		return {
			...experience,
			role: experienceMessage(`${messagePrefix}_role`),
			period: experienceMessage(`${messagePrefix}_period`),
			summary: experienceMessage(`${messagePrefix}_summary`),
			metrics: experienceMessage(`${messagePrefix}_metrics`),
			bullets: experienceMessage(`${messagePrefix}_bullets`).split('|'),
			deliverables: experienceMessage(`${messagePrefix}_deliverables`)
				.split('|')
				.map((text, deliverableIndex) => ({
					id: `d${deliverableIndex + 1}`,
					text,
				})),
		};
	});
}

export const useSohanEmon = () => {
	const [env] = React.useState(() => getAppEnv());

	const skills = useMemo<Skill[]>(
		() => [
			{ name: 'TypeScript', level: 95 },
			{ name: 'React', level: 100 },
			{ name: 'Next.js', level: 100 },
			{ name: 'Go', level: 60 },
			{ name: 'Node.js', level: 85 },
			{ name: 'GraphQL', level: 85 },
			{ name: 'React Native', level: 80 },
			{ name: 'tRPC', level: 90 },
		],
		[],
	);
	const typewriter = useMemo(
		() => [
			m.typewriter_greeting(),
			m.typewriter_intro(),
			m.typewriter_role(),
			m.typewriter_location(),
			m.typewriter_working_style(),
			m.typewriter_project_note(),
		],
		[],
	);
	const education = useMemo<Education>(
		() => ({
			degree: m.education_degree(),
			institution: 'University of Dhaka',
			period: m.education_period(),
			description: m.education_description(),
		}),
		[],
	);

	const experiences = getLocalizedExperiences();

	const recognition = useMemo<Recognition[]>(
		() => [
			{
				title: m.recognition_top_rated_title(),
				platform: 'Upwork',
				year: '2024',
				note: m.recognition_top_rated_note(),
			},
			{
				title: m.recognition_level_two_title(),
				platform: 'Fiverr',
				year: '2024',
				note: m.recognition_level_two_note(),
			},
		],
		[],
	);

	const contact = (() => {
		switch (env) {
			case 'fiverr':
				return {
					label: 'Fiverr',
					href: '/fiverr',
					msg: m.contact_order_fiverr(),
					target: '_blank',
				};

			case 'upwork':
				return {
					label: 'Upwork',
					href: '/upwork',
					msg: m.contact_hire_upwork(),
					target: '_blank',
				};

			default:
				return {
					label: m.nav_contact(),
					href: '/start',
					msg: m.contact_say_hello(),
				};
		}
	})();

	const links: LinkTypes[] = (() => {
		const extraLink: NavItemConfig[] =
			env === 'fiverr'
				? [NAV_BRAND_ITEMS.fiverr]
				: env === 'upwork'
					? [NAV_BRAND_ITEMS.upwork]
					: NAV_FOOTER_ITEMS;

		return [...DEFAULT_LINKS, ...extraLink].map((link) => ({
			...link,
			label: getNavLabel(link.labelKey),
		}));
	})();

	const socials = [
		{
			icon: 'line-md:linkedin',
			label: 'Linkedin',
			url: 'https://linkedin.com/in/sohanemon',
		},
		{
			icon: 'line-md:github-loop',
			label: 'GitHub',
			url: 'https://github.com/sohanemon',
		},
		{
			icon: 'line-md:telegram',
			label: 'Telegram',
			url: 'https://t.me/sohanemon',
		},
		{
			icon: 'line-md:facebook',
			label: 'Facebook',
			url: 'https://facebook.com/m.sohanemon',
		},
		{
			icon: 'line-md:twitter-x-alt',
			label: 'Twitter',
			url: 'https://twitter.com/sohanemon',
		},
	];

	return {
		skills,
		typewriter,
		education,
		links,
		socials,
		experiences,
		recognition,
		contact,
	};
};
